import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyGetProductsQuery } from '../../services/productApi';
import { useGetCategoriesQuery } from '../../services/categoryApi';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 20;
const CATALOG_PAGE_SIZE = 20;

// A tied sort rank (same tier, same in-stock flag) has no guaranteed stable
// order across separate paginated requests, so the same row can occasionally
// come back on two consecutive pages — dedupe on append so neither the
// duplicate React key warning nor a repeated row reaches the cashier.
const appendUnique = (prev, incoming) => {
  const seenIds = new Set(prev.map((p) => p.id));
  return prev.concat(incoming.filter((p) => !seenIds.has(p.id)));
};

const uniqueById = (list) => appendUnique([], list);

export default function useDashboardCatalog({
  selectedTab,
  searchQuery,
  orderCartItems,
  selectedProduct,
  inStockOnly = false,
}) {
  // Category tabs and their product listings come straight from the server —
  // the old approach (grouping a bulk-synced posCache by product.category.name)
  // required the whole catalog in memory just to know which tabs exist, which
  // stopped being viable once org catalogs headed toward 1-15 lakh products.
  const { data: categoriesData, isFetching: isLoadingCategories } = useGetCategoriesQuery();
  // Leaf category names are only unique within their own parent, not org-wide
  // — a client can genuinely have several distinct "2023 LADIES" categories
  // under different parent branches. Only prefix the parent name onto tabs
  // whose leaf name collides with another tab's; a unique name stays plain
  // so the tab strip isn't cluttered for the common case.
  const categories = useMemo(() => {
    const raw = categoriesData?.data || [];
    const nameCounts = raw.reduce((acc, c) => {
      acc[c.name] = (acc[c.name] || 0) + 1;
      return acc;
    }, {});
    return raw.map((c) => ({
      ...c,
      displayName: nameCounts[c.name] > 1 && c.parent_name ? `${c.parent_name} · ${c.name}` : c.name,
    }));
  }, [categoriesData]);
  const activeCategory = categories[selectedTab] || null;

  const [triggerCategoryProducts, { isFetching: isLoadingCategoryProducts }] = useLazyGetProductsQuery();
  const [productsToShow, setProductsToShow] = useState([]);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryHasMore, setCategoryHasMore] = useState(false);
  const [isFetchingMoreCategoryProducts, setIsFetchingMoreCategoryProducts] = useState(false);

  useEffect(() => {
    if (!activeCategory) {
      setProductsToShow([]);
      setCategoryHasMore(false);
      return;
    }
    setProductsToShow([]);
    setCategoryPage(1);
    setCategoryHasMore(false);
    triggerCategoryProducts({ category_id: activeCategory.id, page: 1, per_page: CATALOG_PAGE_SIZE, in_stock_only: inStockOnly }).unwrap()
      .then((res) => {
        setProductsToShow(uniqueById(res?.data || []));
        setCategoryHasMore((res?.pagination?.total_pages || 1) > 1);
      })
      .catch(() => {});
  }, [activeCategory?.id, inStockOnly, triggerCategoryProducts]);

  // Same reasoning as the search dropdown's infinite scroll: a category can
  // hold thousands of products at scale, so fetch more only as the cashier
  // scrolls the list instead of loading a whole category upfront.
  const loadMoreCategoryProducts = useCallback(() => {
    if (!activeCategory || !categoryHasMore || isFetchingMoreCategoryProducts) return;
    const nextPage = categoryPage + 1;
    setIsFetchingMoreCategoryProducts(true);
    triggerCategoryProducts({ category_id: activeCategory.id, page: nextPage, per_page: CATALOG_PAGE_SIZE, in_stock_only: inStockOnly }).unwrap()
      .then((res) => {
        setProductsToShow((prev) => appendUnique(prev, res?.data || []));
        setCategoryPage(nextPage);
        setCategoryHasMore(nextPage < (res?.pagination?.total_pages || 1));
      })
      .catch(() => {})
      .finally(() => setIsFetchingMoreCategoryProducts(false));
  }, [activeCategory, categoryHasMore, isFetchingMoreCategoryProducts, categoryPage, inStockOnly, triggerCategoryProducts]);

  const [triggerProductSearch, { isFetching: isSearchingProducts }] = useLazyGetProductsQuery();
  const [serverSearchResults, setServerSearchResults] = useState(null);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [isFetchingMoreResults, setIsFetchingMoreResults] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchTermRef = useRef('');

  useEffect(() => {
    setServerSearchResults(null); // clear the previous term's results immediately, not just on debounce
    setSearchPage(1);
    setSearchHasMore(false);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    const term = searchQuery.trim();
    searchTermRef.current = term;
    if (term.length < 2) return undefined;

    searchDebounceRef.current = setTimeout(() => {
      triggerProductSearch({ search: term, page: 1, per_page: SEARCH_PAGE_SIZE, in_stock_only: inStockOnly }).unwrap()
        .then((res) => {
          setServerSearchResults(uniqueById(res?.data || []));
          setSearchHasMore((res?.pagination?.total_pages || 1) > 1);
        })
        .catch(() => {});
    }, SEARCH_DEBOUNCE_MS);

    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, inStockOnly, triggerProductSearch]);

  // Dropdown pagination: a match can sit well past the first page for a large
  // SKU family (many size/color variants), and the backend doesn't sort
  // results by relevance/stock — so raising per_page alone doesn't guarantee
  // the right row is visible, it just moves the cutoff. Load more on demand
  // as the cashier scrolls instead of ever fetching hundreds of rows upfront.
  const loadMoreSearchResults = useCallback(() => {
    const term = searchTermRef.current;
    if (term.length < 2 || !searchHasMore || isFetchingMoreResults) return;
    const nextPage = searchPage + 1;
    setIsFetchingMoreResults(true);
    triggerProductSearch({ search: term, page: nextPage, per_page: SEARCH_PAGE_SIZE, in_stock_only: inStockOnly }).unwrap()
      .then((res) => {
        setServerSearchResults((prev) => appendUnique(prev || [], res?.data || []));
        setSearchPage(nextPage);
        setSearchHasMore(nextPage < (res?.pagination?.total_pages || 1));
      })
      .catch(() => {})
      .finally(() => setIsFetchingMoreResults(false));
  }, [triggerProductSearch, searchHasMore, isFetchingMoreResults, searchPage, inStockOnly]);

  const searchResults = serverSearchResults || [];

  const calculateItemTax = useCallback((item) => {
    if (!item) return { rate: 0, taxAmount: 0, total: 0 };
    const backendItem = orderCartItems.find((i) => i.product_id === item.id);
    if (backendItem) {
      const taxAmt = parseFloat(backendItem.tax_amount) || 0;
      const lineTotal = parseFloat(backendItem.line_total) || 0;
      const subtotalBeforeTax = lineTotal - taxAmt;
      const rate = subtotalBeforeTax > 0 ? Math.round((taxAmt / subtotalBeforeTax) * 100) : 0;
      return { rate, taxAmount: taxAmt, total: lineTotal };
    }
    const price = parseFloat(item.retail_price) || 0;
    const qty = parseInt(item.qty) || 1;
    return { rate: 0, taxAmount: 0, total: price * qty };
  }, [orderCartItems]);

  const selectedTaxInfo = useMemo(
    () => calculateItemTax(selectedProduct),
    [selectedProduct, calculateItemTax]
  );

  const getProductStock = useCallback((product) => {
    return product?.stock?.total_quantity ?? 0;
  }, []);

  return {
    categories,
    isLoadingCategories,
    activeCategory,
    productsToShow,
    isLoadingCategoryProducts,
    categoryHasMore,
    isFetchingMoreCategoryProducts,
    loadMoreCategoryProducts,
    searchResults,
    isSearchingProducts,
    searchHasMore,
    isFetchingMoreResults,
    loadMoreSearchResults,
    calculateItemTax,
    selectedTaxInfo,
    getProductStock,
  };
}
